import React from 'react'
import {InteractionManager, StyleSheet, View} from 'react-native'
import type ViewShot from 'react-native-view-shot'
import {Image} from 'expo-image'
import {
  type AppBskyEmbedExternal,
  type AppBskyEmbedImages,
  type AppBskyEmbedVideo,
} from '@atproto/api'

import {isWeb} from '#/platform/detection'
import {
  TellusInstagramShareCard,
  type TellusPost,
} from '#/components/share/TellusInstagramShareCard'
import {parseEmbed} from '#/types/bsky/post'

const LazyViewShot = React.lazy(
  // @ts-expect-error dynamic import
  () => import('react-native-view-shot/src/index'),
)

const SHARE_CARD_WIDTH = 1080
const SHARE_CARD_HEIGHT = 1920
const SHARE_CARD_AVATAR_SIZE = 88
const AVATAR_FULL_PATH = '/img/avatar/plain/'
const AVATAR_THUMB_PATH = '/img/avatar_thumbnail/plain/'

type CaptureFn = (post: TellusPost) => Promise<string>

let captureFn: CaptureFn | null = null
let lastShareCard: {postUri: string; uri: string; mime: 'image/png'} | null =
  null

export function registerShareCardRenderer(fn: CaptureFn | null) {
  captureFn = fn
}

export function getCachedShareCard(post: TellusPost) {
  if (lastShareCard?.postUri === post.uri) {
    return lastShareCard
  }
  return null
}

export async function renderShareCardToPngUri(
  post: TellusPost,
): Promise<{uri: string; mime: 'image/png'}> {
  if (!captureFn) {
    await waitForRenderer()
  }
  if (!captureFn) {
    throw new Error('Share card renderer not ready')
  }
  const uri = await captureFn(post)
  lastShareCard = {postUri: post.uri, uri, mime: 'image/png'}
  return lastShareCard
}

export function TellusInstagramShareCardRenderer() {
  const viewShotRef = React.useRef<ViewShot>(null)
  const webCaptureRef = React.useRef<any>(null)
  const [renderPost, setRenderPost] = React.useState<TellusPost | null>(null)
  const [renderKey, setRenderKey] = React.useState(0)
  const pendingRef = React.useRef<{
    resolve: (uri: string) => void
    reject: (error: unknown) => void
  } | null>(null)
  const prefetchedAssetsRef = React.useRef<{
    postUri: string
    avatarUri?: string
    coverUri?: string
  } | null>(null)
  const readyResolveRef = React.useRef<null | (() => void)>(null)
  const readyPromiseRef = React.useRef<Promise<void> | null>(null)

  React.useEffect(() => {
    registerShareCardRenderer(async post => {
      const prefetched = await prefetchImages(post)
      return new Promise((resolve, reject) => {
        prefetchedAssetsRef.current = {
          postUri: post.uri,
          avatarUri: prefetched.avatarUri,
          coverUri: prefetched.coverUri,
        }
        pendingRef.current = {resolve, reject}
        readyPromiseRef.current = new Promise<void>(readyResolve => {
          readyResolveRef.current = readyResolve
        })
        setRenderPost(post)
        setRenderKey(prev => prev + 1)
      })
    })

    return () => {
      registerShareCardRenderer(null)
      readyResolveRef.current = null
      readyPromiseRef.current = null
      prefetchedAssetsRef.current = null
    }
  }, [])

  React.useEffect(() => {
    if (!renderPost || !pendingRef.current) return
    let cancelled = false

    const capture = async () => {
      try {
        const readyPromise = readyPromiseRef.current
        const readyTimeoutMs =
          renderPost && (getAvatarImage(renderPost) || getCoverImage(renderPost))
            ? 4000
            : 1500
        if (readyPromise) {
          await Promise.race([
            readyPromise,
            new Promise(resolve => setTimeout(resolve, readyTimeoutMs)),
          ])
        }
        await new Promise<void>(resolve => {
          InteractionManager.runAfterInteractions(() => resolve())
        })
        await new Promise<void>(resolve => {
          requestAnimationFrame(() => resolve())
        })
        await new Promise<void>(resolve => {
          setTimeout(resolve, 120)
        })
        await new Promise<void>(resolve => {
          requestAnimationFrame(() => resolve())
        })
        const uri = isWeb
          ? await captureWeb(webCaptureRef.current)
          : await viewShotRef.current?.capture?.()
        if (!uri) {
          throw new Error('Failed to capture share card')
        }
        if (!cancelled) pendingRef.current?.resolve(uri)
      } catch (error) {
        if (!cancelled) pendingRef.current?.reject(error)
      } finally {
        pendingRef.current = null
        readyResolveRef.current = null
        readyPromiseRef.current = null
      }
    }

    capture()

    return () => {
      cancelled = true
    }
  }, [renderPost, renderKey])

  const prefetchedAssets =
    renderPost && prefetchedAssetsRef.current?.postUri === renderPost.uri
      ? prefetchedAssetsRef.current
      : null

  return (
    <View style={styles.container} pointerEvents="none">
      <React.Suspense fallback={null}>
        <LazyViewShot
          // @ts-ignore this library doesn't have types
          ref={viewShotRef}
          options={{
            fileName: 'tellus-instagram-story',
            format: 'png',
            quality: 1,
            width: SHARE_CARD_WIDTH,
            height: SHARE_CARD_HEIGHT,
            result: isWeb ? 'data-uri' : 'tmpfile',
          }}>
          {renderPost ? (
            <View
              collapsable={false}
              key={renderKey}
              ref={webCaptureRef}
              style={styles.webCapture}>
              <TellusInstagramShareCard
                post={renderPost}
                prefetchedAvatarUri={prefetchedAssets?.avatarUri}
                prefetchedCoverUri={prefetchedAssets?.coverUri}
                onReady={() => readyResolveRef.current?.()}
              />
            </View>
          ) : (
            <View
              style={[
                styles.placeholder,
                {width: SHARE_CARD_WIDTH, height: SHARE_CARD_HEIGHT},
              ]}
              collapsable={false}
            />
          )}
        </LazyViewShot>
      </React.Suspense>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: -9999,
    left: -9999,
  },
  webCapture: {
    width: SHARE_CARD_WIDTH,
    height: SHARE_CARD_HEIGHT,
  },
  placeholder: {
    backgroundColor: 'transparent',
  },
})

async function waitForRenderer() {
  for (let i = 0; i < 5 && !captureFn; i++) {
    await new Promise(resolve => setTimeout(resolve, 50))
  }
}

async function prefetchImages(
  post: TellusPost,
): Promise<{avatarUri?: string; coverUri?: string}> {
  const avatarUrl = getAvatarImage(post)
  const coverUrl = getCoverImage(post)
  if (isWeb) {
    const [avatarUri, coverUri] = await Promise.all([
      avatarUrl ? fetchAsDataUri(avatarUrl) : Promise.resolve(undefined),
      coverUrl ? fetchAsDataUri(coverUrl) : Promise.resolve(undefined),
    ])
    return {avatarUri, coverUri}
  }

  const uris = [avatarUrl, coverUrl].filter(Boolean) as string[]
  if (uris.length > 0) {
    try {
      await Image.prefetch(uris)
    } catch {
      // best effort
    }
  }

  return {}
}

function getAvatarImage(post: TellusPost) {
  const avatar = post.author?.avatar
  if (!avatar) return undefined
  if (SHARE_CARD_AVATAR_SIZE < 90) {
    return avatar.replace(AVATAR_FULL_PATH, AVATAR_THUMB_PATH)
  }
  return avatar
}

function getCoverImage(post: TellusPost) {
  if (!post.embed) return undefined

  const parsed = parseEmbed(post.embed)
  const media = parsed.type === 'post_with_media' ? parsed.media : parsed

  if (media.type === 'images') {
    const image = (media.view as AppBskyEmbedImages.View).images[0]
    return image?.thumb || image?.fullsize
  }
  if (media.type === 'video') {
    return (media.view as AppBskyEmbedVideo.View).thumbnail
  }
  if (media.type === 'link') {
    return (media.view as AppBskyEmbedExternal.View).external?.thumb
  }

  return undefined
}

async function fetchAsDataUri(uri: string): Promise<string | undefined> {
  try {
    const response = await fetch(uri)
    if (!response.ok) return undefined
    const blob = await response.blob()
    const dataUri = await new Promise<string | undefined>(resolve => {
      const reader = new FileReader()
      reader.onloadend = () => {
        resolve(typeof reader.result === 'string' ? reader.result : undefined)
      }
      reader.onerror = () => resolve(undefined)
      reader.readAsDataURL(blob)
    })
    return dataUri
  } catch {
    return undefined
  }
}

async function captureWeb(node: any): Promise<string | null> {
  if (!node) return null
  // html2canvas is already a dependency of react-native-view-shot on web.
  // @ts-expect-error no types
  const html2canvas = (await import('html2canvas')).default
  const renderedCanvas = await html2canvas(node, {
    useCORS: true,
    scale: 1,
  })

  if (
    renderedCanvas.width !== SHARE_CARD_WIDTH ||
    renderedCanvas.height !== SHARE_CARD_HEIGHT
  ) {
    const resizedCanvas = document.createElement('canvas')
    resizedCanvas.width = SHARE_CARD_WIDTH
    resizedCanvas.height = SHARE_CARD_HEIGHT
    const context = resizedCanvas.getContext('2d')
    if (!context) {
      return renderedCanvas.toDataURL('image/png', 1)
    }
    context.drawImage(
      renderedCanvas,
      0,
      0,
      SHARE_CARD_WIDTH,
      SHARE_CARD_HEIGHT,
    )
    return resizedCanvas.toDataURL('image/png', 1)
  }

  return renderedCanvas.toDataURL('image/png', 1)
}
